import React, {
  useImperativeHandle,
  forwardRef,
  useState,
  useRef,
} from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
} from '@material-ui/core';

import api from '../../../services/api';
import { SECONDARY_COLOR, ATALHO_AVANCAR, ATALHO_VOLTAR } from '../../../utils';
import { TextInput } from '../../Inputs';

const FECHAR_VENDA_ACTION = ATALHO_AVANCAR;
const CANCELAR_VENDA_ACTION = ATALHO_VOLTAR;
const ESC_ACTION = 'Escape';

const ETAPA_1 = 0;
const ETAPA_2 = 1;

const DialogoAutorizacaoComQrcode = forwardRef(
  ({ handleSalvar, title, enqueueSnackbar, mensagemSucesso = 'Autorizado com sucesso!' }, ref) => {
    const [open, setOpen] = useState(false);
    const [textInit, setTextInit] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [codigo, setCodigo] = useState(0);
    const [dados, setDados] = useState(null);
    const [etapa, setEtapa] = useState(ETAPA_1);
    const refControle = useRef(false);

    const refs = useRef([]);
    refs.current = [React.createRef()];

    const handleCloseDialog = () => {
      setOpen(false);
    };

    useImperativeHandle(ref, () => ({
      handleOpen(codigoNew = 0, dadosNew = null) {
        setOpen(true);
        setCodigo(codigoNew);
        setTextInit('');
        setUsername('');
        setPassword('');
        setDados(dadosNew);
        setEtapa(ETAPA_1);
      },
    }));

    async function handleSalvarDialog() {
      if (!refControle.current) {
        refControle.current = true;
        if (!erroExistente) {
          try {
            const dataConfig = await api.post('/login_retirada_caixa', {
              username,
              password,
            });
            if (dataConfig.data.status) {
              handleSalvar(dataConfig.data.user_id, dados, codigo);
              setOpen(false);
              enqueueSnackbar(mensagemSucesso, { variant: 'success' });
            } else {
              enqueueSnackbar('Senha incorreta!', { variant: 'error' });
            }
          } catch (e) {
            enqueueSnackbar('INTERNET: ERRO AO AUTORIZAR!', { variant: 'error' });
          } finally {
            refControle.current = false;
          }
        }
      }
    }

    function validateTextInit() {
      let error = '';
      if (!textInit) error = 'Este campo é obrigatório';
      return error;
    }

    function validatePassword() {
      let error = '';
      if (!password) error = 'Este campo é obrigatório';
      return error;
    }

    function getErros() {
      return [etapa === ETAPA_1 ? validateTextInit() : validatePassword()];
    }

    const erros = getErros();

    function hasError() {
      for (let i = 0; i < erros.length; i += 1) {
        if (erros[i] !== '') return true;
      }
      return false;
    }

    const erroExistente = hasError();

    async function getDadosQrcode() {
      try {
        const response = await api.post('/dados_qrcode_gerente_caixa', {
          hash: textInit.replace(/;/g, '/'),
        });
        const { status, message, data } = response.data;

        if (status) {
          setTextInit('');
          setUsername(data.username);
          setEtapa(ETAPA_2);
          setPassword('');
        } else {
          setTextInit('');
          enqueueSnackbar(message, { variant: 'error' });
        }
      } catch (e) {
        setTextInit('');
        enqueueSnackbar('INTERNET: ERRO AO AUTORIZAR COM O QRCODE!', { variant: 'error' });
      }
    }

    return (
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        aria-labelledby="form-dialog-title"
        disableBackdropClick
        disableEscapeKeyDown
      >
        <DialogTitle
          id="form-dialog-title"
          style={{ color: 'white', backgroundColor: SECONDARY_COLOR, textAlign: 'center' }}
        >
          {title}
        </DialogTitle>
        <DialogContent>
          {etapa === ETAPA_1 ? (
            <TextInput
              name="qrcode"
              label="Insira aqui o Qrcode"
              value={textInit}
              onChange={(event) => setTextInit(event.target.value)}
              fullWidth
              handleKey={(action, name) => {
                switch (name) {
                  case FECHAR_VENDA_ACTION:
                    getDadosQrcode();
                    break;
                  case CANCELAR_VENDA_ACTION:
                    handleCloseDialog();
                    break;
                  case ESC_ACTION:
                    handleCloseDialog();
                    break;
                  default:
                    break;
                }
              }}
              handleEnter={() => getDadosQrcode()}
              error={erros[0] !== ''}
              helperText={erros[0]}
              autoFocus
            />
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Gerente
              </Typography>

              <Box display="flex">
                <Box flex={1} mr="0.5em">
                  <TextInput
                    name="username"
                    label="Usuário"
                    value={username}
                    onChange={() => {}}
                    fullWidth
                    handleKey={() => {}}
                    handleEnter={() => {}}
                    disabled
                  />
                </Box>
                <Box flex={1} ml="0.5em">
                  <TextInput
                    ref={refs.current[0]}
                    handleEnter={() => handleSalvarDialog()}
                    name="password"
                    type="password"
                    label="Senha"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    fullWidth
                    handleKey={(action, name) => {
                      switch (name) {
                        case FECHAR_VENDA_ACTION:
                          handleSalvarDialog();
                          break;
                        case CANCELAR_VENDA_ACTION:
                          handleCloseDialog();
                          break;
                        case ESC_ACTION:
                          handleCloseDialog();
                          break;
                        default:
                          break;
                      }
                    }}
                    error={erros[0] !== ''}
                    helperText={erros[0]}
                    autoFocus
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={handleCloseDialog} color="secondary" variant="contained">
            {`Cancelar (${CANCELAR_VENDA_ACTION})`}
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={etapa === ETAPA_1 ? getDadosQrcode : handleSalvarDialog}
            disabled={erroExistente}
          >
            {`Confirmar (${FECHAR_VENDA_ACTION})`}
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default DialogoAutorizacaoComQrcode;
